import React, { useState } from "react";
import {
  Container,
  createGenerateClassName,
  createTheme,
  StylesProvider,
  ThemeProvider,
  Typography,
  Grid,
  withStyles,
  Box,
  Button,
} from "@material-ui/core";
import NavBar from "../../components/NavBar/NavBar";
import {
  DATE_FORMAT_DEFAULT,
  MODAL_TYPES,
  NAV_BARS,
  TIME_FORMAT,
  USER_ROLES,
} from "../../utils/constants";
import LessonCard from "../../components/LessonCard";
import { formatInTimeZone, getDayOfWeek } from "../../utils/dateTime";
import UserCard from "../../components/UserCard";
import SharedSessionCard from "../../components/SharedSessionCard";
import SearchInput from "../../components/SearchInput/SearchInput";
import SignIn from "../../components/Modal/SignIn";
import FollowUpsToday from "../../components/FollowUpsToday";
import FirstClassPricing from "../../components/FirstClassPricing";
import BusinessPulse from "../../components/BusinessPulse";
import AddLessonModal from "../../components/Modal/AddLesson";
import usersApi from "../../apis/usersApi";
import leadsApi from "../../apis/leadsApi";
import SplashLanding from "../../components/SplashLanding";
import WeekAgenda from "../../components/WeekAgenda";
import CheckInToday from "../../components/CheckInToday";
import NextShowBanner from "../../components/NextShowBanner";
import FamilyHub from "../../components/FamilyHub";
import EventIcon from "@material-ui/icons/Event";
import GroupIcon from "@material-ui/icons/Group";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";

const generateClassName = createGenerateClassName({
  productionPrefix: "c",
  disableGlobal: true,
  seed: "home",
});

const theme = createTheme({
  typography: {
    fontFamily: "'Nunito', 'Varela Round', sans-serif",
  },
  overrides: {
    MuiFormLabel: {
      root: {
        "&$focused": {
          color: "#333333",
        },
      },
    },
    MuiInput: {
      underline: {
        "&::after": {
          borderBottom: "2px solid #333333",
        },
      },
    },
  },
});

const LessonListBox = withStyles(() => ({
  root: {
    borderTop: "1px solid #E0E0E0",
    flexGrow: 1,
    paddingBottom: 32,
  },
}))(Box);

const LessonEmptyBox = withStyles(() => ({
  root: {
    textAlign: "center",
    padding: "24px 0",
  },
}))(Box);

const NoData = withStyles(() => ({
  root: {
    fontSize: 16,
    lineHeight: "21px",
  },
}))(Typography);

const HeroCard = withStyles((theme) => ({
  root: {
    background: "linear-gradient(135deg, #FAFBFD 0%, #F4F8FF 100%)",
    border: "1px solid #E0E0E0",
    borderRadius: 16,
    boxShadow: "0 4px 6px rgba(0,0,0,.05)",
    padding: "36px 40px",
    marginBottom: 24,
    display: "flex",
    alignItems: "center",
    gap: 32,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      padding: 24,
      gap: 16,
    },
  },
}))(Box);

const HeroIllustration = withStyles((theme) => ({
  root: {
    flexShrink: 0,
    width: 180,
    height: 180,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      width: 120,
      height: 120,
    },
    "& .bg-blob": {
      position: "absolute",
      inset: 0,
      borderRadius: 36,
      background: "linear-gradient(135deg, #E8F1FF 0%, #DFEBFF 100%)",
      transform: "rotate(-6deg)",
    },
    "& .accent-dot": {
      position: "absolute",
      borderRadius: "50%",
    },
    "& .accent-dot-1": {
      width: 18, height: 18, top: 8, right: 16, background: "#FFCB6B",
    },
    "& .accent-dot-2": {
      width: 12, height: 12, bottom: 24, left: 8, background: "#00695F",
    },
    "& .accent-dot-3": {
      width: 10, height: 10, top: 28, left: -4, background: "#007BFF",
    },
    "& .main-icon": {
      position: "relative",
      width: 88,
      height: 88,
      borderRadius: 24,
      background: "#ffffff",
      boxShadow: "0 8px 20px rgba(0, 123, 255, .15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#007BFF",
      "& svg": { fontSize: 42 },
    },
  },
}))(Box);

const HeroContent = withStyles(() => ({
  root: { flex: 1, minWidth: 0 },
}))(Box);

const HeroHeading = withStyles((theme) => ({
  root: {
    fontSize: 28,
    fontWeight: 600,
    color: "#1a1a1a",
    marginBottom: 8,
    lineHeight: 1.15,
    [theme.breakpoints.down("sm")]: {
      fontSize: 22,
      textAlign: "center",
    },
  },
}))(Typography);

const HeroSubheading = withStyles((theme) => ({
  root: {
    fontSize: 15,
    color: "#4F4F4F",
    marginBottom: 20,
    lineHeight: 1.5,
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
      textAlign: "center",
    },
  },
}))(Typography);

const HeroButtonRow = withStyles((theme) => ({
  root: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
    },
  },
}))(Box);

const HeroPrimaryButton = withStyles(() => ({
  root: {
    background: "#1a1a1a",
    color: "#ffffff",
    borderRadius: 140,
    textTransform: "none",
    padding: "12px 22px",
    fontSize: 14,
    fontWeight: 600,
    boxShadow: "none",
    "&:hover": { background: "#000000" },
  },
}))(Button);

const HeroSecondaryButton = withStyles(() => ({
  root: {
    background: "#ffffff",
    color: "#1a1a1a",
    border: "1px solid #1a1a1a",
    borderRadius: 140,
    textTransform: "none",
    padding: "12px 22px",
    fontSize: 14,
    fontWeight: 600,
    boxShadow: "none",
    "&:hover": { background: "#F8F9FA" },
  },
}))(Button);

const SectionLabel = withStyles(() => ({
  root: {
    fontSize: 12,
    fontWeight: 600,
    color: "#666666",
    marginBottom: 12,
  },
}))(Typography);

const QuickActionCard = withStyles((theme) => ({
  root: {
    background: "#ffffff",
    border: "1px solid #E0E0E0",
    borderRadius: 16,
    boxShadow: "0 4px 6px rgba(0,0,0,.05)",
    padding: 18,
    cursor: "pointer",
    transition: "all 0.15s ease",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    // Equal heights come from the grid item being a flex container and the
    // card filling it. `height: 100%` did the same on desktop rows but on a
    // phone, where items stack, it resolved against a height the content
    // had already outgrown, and the cards overlapped.
    flex: 1,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 16px rgba(0,0,0,.08)",
      borderColor: "#BDBDBD",
    },
  },
}))(Box);

const QuickActionIcon = withStyles(() => ({
  root: {
    width: 36,
    height: 36,
    borderRadius: 12,
    background: "#F4F6F8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#1a1a1a",
    "& svg": { fontSize: 20 },
  },
}))(Box);

const QuickActionTitle = withStyles(() => ({
  root: {
    fontSize: 14,
    fontWeight: 600,
    color: "#1a1a1a",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}))(Typography);

const QuickActionDesc = withStyles(() => ({
  root: {
    fontSize: 12,
    color: "#666666",
    lineHeight: 1.4,
  },
}))(Typography);

const greetingForTime = () => {
  const hr = new Date().getHours();
  if (hr < 12) return "morning";
  if (hr < 18) return "afternoon";
  return "evening";
};

const Title = withStyles(() => ({
  root: {
    fontSize: 30,
    margin: "24px 0",
    fontWeight: 500,
    [theme.breakpoints.down("sm")]: {
      fontSize: 20,
    },
  },
}))(Typography);

const LessonListStatus = withStyles(() => ({
  root: {
    margin: "36px 0 24px 0",
    fontSize: 30,
    lineHeight: "40px",
    fontWeight: 400,
    color: "#000000",
    display: "flex",
    textTransform: "uppercase",
    [theme.breakpoints.down("sm")]: {
      fontSize: 20,
      margin: "20px 0 16px 0",
    },
  },
}))(Typography);

const UserList = withStyles(() => ({
  root: {
    flexWrap: "nowrap",
    overflowX: "auto",
  },
}))(Grid);

// The owner's whole team at once, wrapping onto a second row, instead of a
// strip that scrolls sideways past the edge (Stefan, call 10: "the
// instructors should not be scrollable... put all the instructors in").
const TeamList = withStyles(() => ({
  root: {
    flexWrap: "wrap",
  },
}))(Grid);

const UserItem = withStyles((theme) => ({
  root: {
    minWidth: "16.666667%",
    [theme.breakpoints.down("sm")]: {
      minWidth: "50%",
    },
  },
}))(Grid);

const ShowMoreBox = withStyles(() => ({
  root: {
    width: "100%",
    textAlign: "center",
    margin: "16px 0",
  },
}))(Box);

const ShowMoreButton = withStyles(() => ({
  root: {
    borderRadius: 142,
    border: "1px solid #000000",
    backgroundColor: "#ffffff",
    padding: "8px 12px",
    color: "#000000",
    "&:hover": { backgroundColor: "#000000", color: "#ffffff" },
  },
}))(Button);

const Home = () => {
  const current_user = window.current_user || {};
  const { id, first_name, last_name, role } = current_user;
  const upcoming_lessons = window.upcoming_lessons || {};
  const { data: upComingLessonList = [], included: upComingLessonIncluded = [] } =
    upcoming_lessons;
  const shared_sessions = window.shared_sessions || {};
  const { data: sharedSessionList = [], included: sharedSessionIncluded = [] } =
    shared_sessions;
  const recent_lessons = window.recent_lessons || {};
  const { data: recentLessonList = [], included: recentLessonIncluded = [] } =
    recent_lessons;
  const myInstructorList = window.my_instructor || [];
  const myStudentList = window.my_student || [];
  const studioChecklist = window.studio_checklist || null;
  const nextShow = window.next_show || null;
  // A family's classes, next show and bills (student logins only).
  const familyHub = window.family_hub || null;
  // Onboarding asked how the studio mostly runs — the first-run hero should
  // lead with a class for group studios, a lesson for private ones.
  const primaryFormat = current_user.studio_primary_format || "both";
  const runsClasses = primaryFormat === "group" || primaryFormat === "both";

  // The first-run hero owns the fully-blank state; the week agenda strip
  // only appears once there is anything (people or lessons) to anchor it.
  const isBlankCanvas =
    upComingLessonList.length === 0 &&
    recentLessonList.length === 0 &&
    Array.isArray(myInstructorList) &&
    myInstructorList.length === 0 &&
    Array.isArray(myStudentList) &&
    myStudentList.length === 0;

  const [upcomingLessonMaxShow, setUpcomingLessonMaxShow] = useState(6);
  const [recentLessonMaxShow, setRecentLessonMaxShow] = useState(6);
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // A "Start Free Trial" click from swyvel.io arrives with ?start=trial —
  // open the signup modal on load so high-intent visitors skip a second
  // pitch and land straight on the form.
  const wantsTrial =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("start") === "trial";
  const [isShowSignInModal, setIsShowSignInModal] = useState(!id && wantsTrial);
  const [isOpenAddLessonModal, setIsOpenAddLessonModal] = useState(false);
  const [students, setStudents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [isFetchedStudentAndInstructor, setIsFetchedStudentAndInstructor] = useState(false);
  const [sampleDataBusy, setSampleDataBusy] = useState(false);

  // Seed or remove the clearly-marked sample studio (roster, classes,
  // calendar sessions) so a cold owner can feel the product working.
  const toggleSampleData = async (method) => {
    if (sampleDataBusy) return;
    setSampleDataBusy(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      await fetch("/sample_data", { method, headers: { "X-CSRF-Token": csrfToken } });
      window.location.reload();
    } catch (e) {
      setSampleDataBusy(false);
    }
  };

  const amountLessonPerPage = 6;

  const findCreatorAvatar = (creatorId) => {
    const creator = sharedSessionIncluded.find(
      (includedItem) => includedItem.id === creatorId && includedItem.type === "user"
    );
    return creator ? creator.attributes.avatar_url : null;
  };

  // Function to open Add Lesson modal (used by onboarding checklist)
  const openAddLessonModal = async () => {
    try {
      if (!isFetchedStudentAndInstructor) {
        // Fetch both User students and Lead contacts
        const [studentsRes, leadsRes] = await Promise.all([
          usersApi.getStudents(),
          leadsApi.getList()
        ]);

        if (Array.isArray(studentsRes) && studentsRes.length > 0) {
          setStudents(studentsRes);
        }

        // Add leads to the list if any exist
        if (leadsRes && Array.isArray(leadsRes.data)) {
          const leadContacts = leadsRes.data.map(lead => ({
            id: lead.id,
            type: 'lead',
            attributes: {
              full_name: `${lead.attributes.first_name || ''} ${lead.attributes.last_name || ''}`.trim(),
              first_name: lead.attributes.first_name,
              last_name: lead.attributes.last_name,
              email: lead.attributes.email,
              phone: lead.attributes.phone,
            }
          }));
          setStudents(prev => [...prev, ...leadContacts]);
        }

        const instructorsRes = await usersApi.getInstructors();
        if (Array.isArray(instructorsRes) && instructorsRes.length > 0) {
          setInstructors(instructorsRes);
        }
      }

      setIsFetchedStudentAndInstructor(true);
      setIsOpenAddLessonModal(true);
    } catch (error) {
      console.error('Error opening Add Lesson modal:', error);
      setIsFetchedStudentAndInstructor(false);
    }
  };

  // Make openAddLessonModal available globally for the checklist
  if (typeof window !== 'undefined') {
    window.openAddLessonModal = openAddLessonModal;
  }

  return (
    <>
      <StylesProvider generateClassName={generateClassName}>
        <ThemeProvider theme={theme}>
          {id ? (
            <>
              <Container>
                <Title variant={"h2"}>Hi, {`${first_name}`}</Title>
                <NavBar active={NAV_BARS.HOME} />

                {/* Something changed in Swyvel since they last looked: one
                    line, once. Never there for a student. */}

                {/* Sample-data mode banner — always visible while demo records exist */}
                {role === USER_ROLES.STUDIO && studioChecklist && studioChecklist.sample_data && (
                  <Box
                    style={{
                      background: "#FFFBEB",
                      border: "1px solid #FDE68A",
                      borderRadius: 12,
                      padding: "12px 18px",
                      marginBottom: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 10,
                    }}
                  >
                    <Box>
                      <Typography style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>
                        Sample data is live, see your studio in action:
                      </Typography>
                      <Typography style={{ fontSize: 13, color: "#4F4F4F", marginTop: 2 }}>
                        {[
                          { label: "Contacts", href: "/leads" },
                          { label: "Classes", href: "/studio_classes" },
                          { label: "Show playbook", href: "/recitals" },
                          { label: "My calendar", href: "/lessons/view_calendar" },
                          { label: "Today's attendance", href: "/attendance/today" },
                        ].map((l, i) => (
                          <React.Fragment key={l.href}>
                            {i > 0 && " · "}
                            <a href={l.href} style={{ color: "#007BFF", fontWeight: 600, textDecoration: "none" }}>
                              {l.label}
                            </a>
                          </React.Fragment>
                        ))}
                        {", everything marked \"(Sample)\" is demo content and doesn't count toward your setup."}
                      </Typography>
                    </Box>
                    <Button
                      onClick={() => toggleSampleData("DELETE")}
                      disabled={sampleDataBusy}
                      style={{
                        border: "1px solid #1a1a1a",
                        borderRadius: 140,
                        textTransform: "none",
                        padding: "4px 14px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#1a1a1a",
                        background: "#ffffff",
                      }}
                    >
                      {sampleDataBusy ? "Removing…" : "Remove sample data"}
                    </Button>
                  </Box>
                )}

                {/* The setup checklist used to sit here as a full-width card and
                    push the day's actual content down. It's the corner pill now
                    (SetupPill, mounted in NavBar), which follows owners to the
                    pages where the setup work actually happens. */}

                {/* A class studio with no classes yet gets the pricing
                    question here, where they actually land, rather than
                    buried in Help & Guides where most owners never go. */}
                {role === USER_ROLES.STUDIO && runsClasses &&
                  studioChecklist && (studioChecklist.classes_count || 0) === 0 && (
                    <FirstClassPricing />
                )}

                {role === USER_ROLES.STUDENT && <FamilyHub hub={familyHub} />}

                {/* A teacher's classes today, one tap from check-in. */}
                {role === USER_ROLES.INSTRUCTOR && window.current_user?.checks_in_classes && <CheckInToday />}

                {!isBlankCanvas && <WeekAgenda />}

                {role === USER_ROLES.STUDIO && <NextShowBanner show={nextShow} />}

                {/* The revenue engine's morning list — owners and teachers
                    both make calls. Renders nothing when nobody's due. */}
                {(role === USER_ROLES.STUDIO || role === USER_ROLES.INSTRUCTOR) && (
                  <FollowUpsToday />
                )}

                {/* Owner's glance strip — routes to the full Business overview */}
                {role === USER_ROLES.STUDIO && <BusinessPulse />}

                {/* A family with no private lessons has nothing to search or list here;
                    their classes and bills are in FamilyHub above. */}
                {!(role === USER_ROLES.STUDENT && familyHub && isBlankCanvas) && (
                <LessonListBox>
                  <SearchInput />
                  {/* The blank-canvas hero is the owner's first steps; a family's
                      home is FamilyHub above, which says what to expect. */}
                  {isBlankCanvas && !(role === USER_ROLES.STUDENT && familyHub) && (
                      <>
                        <HeroCard className="juice-in">
                          <HeroIllustration>
                            <div className="bg-blob" />
                            <div className="accent-dot accent-dot-1" />
                            <div className="accent-dot accent-dot-2" />
                            <div className="accent-dot accent-dot-3" />
                            <div className="main-icon">
                              <CalendarTodayIcon />
                            </div>
                          </HeroIllustration>
                          <HeroContent>
                            <HeroHeading>
                              Good {greetingForTime()}, {first_name}, your week's a blank canvas
                            </HeroHeading>
                            {/* "Schedule a lesson" with zero students dead-ends into an
                                "add a student first" modal, so until someone exists,
                                adding students IS the primary action. */}
                            {(() => {
                              const noPeople =
                                studioChecklist &&
                                (studioChecklist.leads_count || 0) +
                                  (studioChecklist.students_count || 0) === 0;
                              if (primaryFormat === "group") {
                                return (
                                  <>
                                    <HeroSubheading>
                                      Create your first class to bring your studio to life, enrollment, attendance, and tuition all flow from it.
                                    </HeroSubheading>
                                    <HeroButtonRow>
                                      <HeroPrimaryButton href="/studio_classes?new=1">
                                        Create your first class
                                      </HeroPrimaryButton>
                                      <HeroSecondaryButton href="/leads">
                                        Add your students
                                      </HeroSecondaryButton>
                                    </HeroButtonRow>
                                  </>
                                );
                              }
                              if (noPeople) {
                                return (
                                  <>
                                    <HeroSubheading>
                                      Start by adding the people you teach, then lessons, classes, and billing all click into place.
                                    </HeroSubheading>
                                    <HeroButtonRow>
                                      <HeroPrimaryButton href="/leads">
                                        Add your students
                                      </HeroPrimaryButton>
                                      {primaryFormat === "both" && (
                                        <HeroSecondaryButton href="/studio_classes?new=1">
                                          Create a class
                                        </HeroSecondaryButton>
                                      )}
                                    </HeroButtonRow>
                                  </>
                                );
                              }
                              return (
                                <>
                                  <HeroSubheading>
                                    Your students are in, schedule your first lesson to bring the calendar to life.
                                  </HeroSubheading>
                                  <HeroButtonRow>
                                    <HeroPrimaryButton onClick={openAddLessonModal}>
                                      Schedule your first lesson
                                    </HeroPrimaryButton>
                                    <HeroSecondaryButton href="/leads">
                                      Add more students
                                    </HeroSecondaryButton>
                                  </HeroButtonRow>
                                </>
                              );
                            })()}
                            {/* Only offer sample data while the studio has no REAL
                                records (checklist counts exclude samples), once
                                actual students or classes exist, demo noise would
                                just get in the way. */}
                            {role === USER_ROLES.STUDIO &&
                              studioChecklist &&
                              !studioChecklist.sample_data &&
                              (studioChecklist.leads_count || 0) === 0 &&
                              (studioChecklist.classes_count || 0) === 0 && (
                              <Typography
                                onClick={() => toggleSampleData("POST")}
                                style={{
                                  marginTop: 12,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#007BFF",
                                  cursor: "pointer",
                                }}
                              >
                                {sampleDataBusy
                                  ? "Setting up your sample studio…"
                                  : "…or explore with sample data first"}
                              </Typography>
                            )}
                          </HeroContent>
                        </HeroCard>

                        <Box style={{ marginTop: 28, marginBottom: 16 }}>
                          <SectionLabel>Explore Swyvel</SectionLabel>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3} style={{ display: "flex" }}>
                              {runsClasses ? (
                                <QuickActionCard className="juice-in juice-in-1" onClick={() => (window.location.href = "/studio_classes")}>
                                  <QuickActionIcon><EventIcon /></QuickActionIcon>
                                  <QuickActionTitle>
                                    Run group classes
                                    <ArrowForwardIcon style={{ fontSize: 16, color: "#666666" }} />
                                  </QuickActionTitle>
                                  <QuickActionDesc>Set up Ballet, Tap, or any recurring class, track enrollments and capacity.</QuickActionDesc>
                                </QuickActionCard>
                              ) : (
                                <QuickActionCard className="juice-in juice-in-1" onClick={() => (window.location.href = "/products")}>
                                  <QuickActionIcon><EventIcon /></QuickActionIcon>
                                  <QuickActionTitle>
                                    Sell lesson packages
                                    <ArrowForwardIcon style={{ fontSize: 16, color: "#666666" }} />
                                  </QuickActionTitle>
                                  <QuickActionDesc>Bundle lessons into packages students buy once and redeem as they book.</QuickActionDesc>
                                </QuickActionCard>
                              )}
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} style={{ display: "flex" }}>
                              <QuickActionCard className="juice-in juice-in-2" onClick={() => (window.location.href = "/families")}>
                                <QuickActionIcon><GroupIcon /></QuickActionIcon>
                                <QuickActionTitle>
                                  Manage families
                                  <ArrowForwardIcon style={{ fontSize: 16, color: "#666666" }} />
                                </QuickActionTitle>
                                <QuickActionDesc>Group parents and kids in one record, perfect for studios with sibling dancers.</QuickActionDesc>
                              </QuickActionCard>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} style={{ display: "flex" }}>
                              <QuickActionCard className="juice-in juice-in-3" onClick={() => (window.location.href = "/bulletins")}>
                                <QuickActionIcon><NotificationsActiveIcon /></QuickActionIcon>
                                <QuickActionTitle>
                                  Send a bulletin
                                  <ArrowForwardIcon style={{ fontSize: 16, color: "#666666" }} />
                                </QuickActionTitle>
                                <QuickActionDesc>Message all your students at once, class updates, reminders, or announcements.</QuickActionDesc>
                              </QuickActionCard>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3} style={{ display: "flex" }}>
                              <QuickActionCard className="juice-in juice-in-4" onClick={() => (window.location.href = `/studios/${current_user.manage_studio_id}/my_business/overview`)}>
                                <QuickActionIcon><TrendingUpIcon /></QuickActionIcon>
                                <QuickActionTitle>
                                  Business overview
                                  <ArrowForwardIcon style={{ fontSize: 16, color: "#666666" }} />
                                </QuickActionTitle>
                                <QuickActionDesc>Track lessons, revenue, leads, and class utilization at a glance.</QuickActionDesc>
                              </QuickActionCard>
                            </Grid>
                          </Grid>
                        </Box>
                      </>
                    )}
                  {upComingLessonList.length > 0 && (
                    <>
                      <LessonListStatus>Upcoming Lesson</LessonListStatus>
                      <Grid container spacing={2}>
                        {upComingLessonList.map((item, index) => {
                          const { status, end_time, start_time } =
                            item.attributes;
                          const {
                            instructor: instructorRelated,
                            studio: studioRelated,
                            studio_address: studioAddressRelated,
                            student: studentRelated,
                          } = item.relationships;
                          const startDate = start_time
                            ? formatInTimeZone(
                              start_time,
                              DATE_FORMAT_DEFAULT,
                              clientTimeZone
                            )
                            : "";
                          const startHour = start_time
                            ? formatInTimeZone(
                              start_time,
                              TIME_FORMAT,
                              clientTimeZone
                            )
                            : "";
                          const endHour = end_time
                            ? formatInTimeZone(
                              end_time,
                              TIME_FORMAT,
                              clientTimeZone
                            )
                            : "";
                          const instructor = upComingLessonIncluded.find(
                            (e) =>
                              instructorRelated &&
                              instructorRelated.data &&
                              instructorRelated.data.id &&
                              e.id === instructorRelated.data.id
                          );
                          const student = upComingLessonIncluded.find(
                            (e) =>
                              studentRelated &&
                              studentRelated.data &&
                              studentRelated.data.id &&
                              e.id === studentRelated.data.id
                          );
                          const studio = upComingLessonIncluded.find(
                            (e) =>
                              studioRelated &&
                              studioRelated.data &&
                              studioRelated.data.id &&
                              e.id === studioRelated.data.id
                          );
                          const studioAddress = upComingLessonIncluded.find(
                            (e) =>
                              studioAddressRelated &&
                              studioAddressRelated.data &&
                              studioAddressRelated.data.id &&
                              e.id === studioAddressRelated.data.id
                          );
                          // Lead-based lessons have no User record in
                          // `included` — fall back to the lesson's own
                          // person names so the avatar shows initials
                          // instead of "?". (NOT name_of_instructor — the
                          // rename flow stores custom lesson titles there.)
                          const user =
                            role === USER_ROLES.STUDENT
                              ? {
                                role: "Instructor",
                                full_name:
                                  (instructor &&
                                    instructor.attributes &&
                                    instructor.attributes.first_name) ||
                                  item.attributes.instructor_full_name,
                                avatar_url:
                                  instructor &&
                                  instructor.attributes &&
                                  instructor.attributes.avatar_url,
                              }
                              : {
                                role: "Student",
                                full_name:
                                  (student &&
                                    student.attributes &&
                                    student.attributes.first_name) ||
                                  item.attributes.student_name,
                                avatar_url:
                                  student &&
                                  student.attributes &&
                                  student.attributes.avatar_url,
                              };
                          const dayOfWeek = getDayOfWeek(start_time);

                          const studioAddressText =
                            studioAddress && studioAddress.attributes
                              ? `${studioAddress.attributes.street}, ${studioAddress.attributes.city}, ${studioAddress.attributes.country}`
                              : "";

                          if (index < upcomingLessonMaxShow) {
                            return (
                              <LessonCard
                                key={`lesson-card-${item.id}`}
                                id={item.id}
                                status={status}
                                studioName={
                                  studio && studio.attributes
                                    ? studio.attributes.name
                                    : ""
                                }
                                studioAddress={studioAddressText}
                                user={user}
                                startDate={startDate}
                                startHour={startHour}
                                endHour={endHour}
                                dayOfWeek={dayOfWeek}
                              />
                            );
                          }
                        })}
                      </Grid>
                      {upcomingLessonMaxShow < upComingLessonList.length && (
                        <ShowMoreBox>
                          <ShowMoreButton
                            onClick={() =>
                              setUpcomingLessonMaxShow(
                                upcomingLessonMaxShow + amountLessonPerPage
                              )
                            }
                          >
                            Show more
                          </ShowMoreButton>
                        </ShowMoreBox>
                      )}
                    </>
                  )}
                  {recentLessonList.length > 0 && (
                    <>
                      <LessonListStatus>Recent Lessons</LessonListStatus>
                      <Grid container spacing={2}>
                        {recentLessonList.map((item, index) => {
                          const {
                            student_name,
                            name_of_instructor,
                            status,
                            end_time,
                            start_time,
                          } = item.attributes;
                          const {
                            instructor: instructorRelated,
                            studio: studioRelated,
                            studio_address: studioAddressRelated,
                            student: studentRelated,
                          } = item.relationships;
                          const startDate = start_time
                            ? formatInTimeZone(
                              start_time,
                              DATE_FORMAT_DEFAULT,
                              clientTimeZone
                            )
                            : "";
                          const startHour = start_time
                            ? formatInTimeZone(
                              start_time,
                              TIME_FORMAT,
                              clientTimeZone
                            )
                            : "";
                          const endHour = end_time
                            ? formatInTimeZone(
                              end_time,
                              TIME_FORMAT,
                              clientTimeZone
                            )
                            : "";
                          const instructor = recentLessonIncluded.find(
                            (e) =>
                              instructorRelated &&
                              instructorRelated.data &&
                              instructorRelated.data.id &&
                              e.id === instructorRelated.data.id
                          );
                          const student = recentLessonIncluded.find(
                            (e) =>
                              studentRelated &&
                              studentRelated.data &&
                              studentRelated.data.id &&
                              e.id === studentRelated.data.id
                          );
                          const studio = recentLessonIncluded.find(
                            (e) =>
                              studioRelated &&
                              studioRelated.data &&
                              studioRelated.data.id &&
                              e.id === studioRelated.data.id
                          );
                          const studioAddress = recentLessonIncluded.find(
                            (e) =>
                              studioAddressRelated &&
                              studioAddressRelated.data &&
                              studioAddressRelated.data.id &&
                              e.id === studioAddressRelated.data.id
                          );
                          // Same lead-lesson fallback as the upcoming list
                          // (instructor_full_name, never name_of_instructor —
                          // that field can hold custom lesson titles).
                          const user =
                            role === USER_ROLES.STUDENT
                              ? {
                                role: "Instructor",
                                full_name:
                                  (instructor &&
                                    instructor.attributes &&
                                    instructor.attributes.first_name) ||
                                  item.attributes.instructor_full_name,
                                avatar_url:
                                  instructor &&
                                  instructor.attributes &&
                                  instructor.attributes.avatar_url,
                              }
                              : {
                                role: "Student",
                                full_name:
                                  (student &&
                                    student.attributes &&
                                    student.attributes.first_name) ||
                                  student_name,
                                avatar_url:
                                  student &&
                                  student.attributes &&
                                  student.attributes.avatar_url,
                              };
                          const dayOfWeek = getDayOfWeek(start_time);

                          const studioAddressText =
                            studioAddress && studioAddress.attributes
                              ? `${studioAddress.attributes.street}, ${studioAddress.attributes.city}, ${studioAddress.attributes.country}`
                              : "";

                          if (index < recentLessonMaxShow) {
                            return (
                              <LessonCard
                                key={`lesson-card-${item.id}`}
                                name={
                                  role === USER_ROLES.STUDENT
                                    ? student_name
                                    : name_of_instructor
                                }
                                id={item.id}
                                status={status}
                                studioName={
                                  studio && studio.attributes
                                    ? studio.attributes.name
                                    : ""
                                }
                                studioAddress={studioAddressText}
                                user={user}
                                startDate={startDate}
                                startHour={startHour}
                                endHour={endHour}
                                dayOfWeek={dayOfWeek}
                              />
                            );
                          }
                        })}
                      </Grid>
                      {recentLessonMaxShow < recentLessonList.length && (
                        <ShowMoreBox>
                          <ShowMoreButton
                            onClick={() =>
                              setRecentLessonMaxShow(
                                recentLessonMaxShow + amountLessonPerPage
                              )
                            }
                          >
                            Show more
                          </ShowMoreButton>
                        </ShowMoreBox>
                      )}
                    </>
                  )}

                  {sharedSessionList.length > 0 && (
                    <>
                      <LessonListStatus>Shared Sessions</LessonListStatus>
                      <UserList container spacing={3}>
                        {sharedSessionList.map((session) => {
                          const { id, attributes: { title }, relationships: { creator } } = session;
                          const avatar_url = findCreatorAvatar(creator.data.id);
                          return (
                            <UserItem item xs={6} sm={6} md={2} key={`shared_session_${id}`}>
                              <SharedSessionCard
                                id={id}
                                title={title}
                                avatar_url={avatar_url}
                              />
                            </UserItem>
                          );
                        })}
                      </UserList>
                    </>
                  )}

                  {role === USER_ROLES.STUDENT ? (
                    <>
                      {Array.isArray(myInstructorList) &&
                        myInstructorList.length > 0 && (
                          <>
                            <LessonListStatus>My instructors</LessonListStatus>
                            <UserList container spacing={3}>
                              {myInstructorList.map((instructor) => {
                                const { avatar_url, first_name } =
                                  instructor.attributes;
                                return (
                                  <UserItem
                                    item
                                    xs={6}
                                    sm={6}
                                    md={2}
                                    key={`instructor_${instructor.id}`}
                                  >
                                    <UserCard
                                      id={instructor.id}
                                      avatar_url={avatar_url}
                                      full_name={first_name}
                                    />
                                  </UserItem>
                                );
                              })}
                            </UserList>
                          </>
                        )}
                    </>
                  ) : (
                    <>
                      {Array.isArray(myInstructorList) &&
                        myInstructorList.length > 0 && (
                          <>
                            <LessonListStatus>My instructors</LessonListStatus>
                            <TeamList container spacing={3} data-testid="home-team">
                              {myInstructorList.map((instructor) => {
                                const { avatar_url, first_name } =
                                  instructor.attributes;
                                return (
                                  <Grid
                                    item
                                    xs={4}
                                    sm={3}
                                    md={2}
                                    key={`instructor_${instructor.id}`}
                                  >
                                    <UserCard
                                      id={instructor.id}
                                      avatar_url={avatar_url}
                                      full_name={first_name}
                                    />
                                  </Grid>
                                );
                              })}
                            </TeamList>
                          </>
                        )}
                      {Array.isArray(myStudentList) &&
                        myStudentList.length > 0 && (
                          <>
                            <LessonListStatus>My students</LessonListStatus>
                            <UserList container spacing={3}>
                              {myStudentList.map((student) => {
                                const { avatar_url, first_name } =
                                  student.attributes;
                                return (
                                  <UserItem
                                    item
                                    xs={6}
                                    sm={6}
                                    md={2}
                                    key={`student_${student.id}`}
                                  >
                                    <UserCard
                                      id={student.id}
                                      avatar_url={avatar_url}
                                      full_name={first_name}
                                    />
                                  </UserItem>
                                );
                              })}
                            </UserList>
                          </>
                        )}
                    </>
                  )}
                </LessonListBox>
                )}
              </Container>
            </>
          ) : (
            <SplashLanding onStartTrial={() => setIsShowSignInModal(true)} />
          )}
        </ThemeProvider>
      </StylesProvider>

      {isShowSignInModal && (
        <SignIn
          isOpen={isShowSignInModal}
          onClose={() => setIsShowSignInModal(false)}
          initialType={MODAL_TYPES.SIGN_UP}
        />
      )}

      {isOpenAddLessonModal && (
        <AddLessonModal
          isOpen={isOpenAddLessonModal}
          students={students}
          instructors={instructors}
          studio_id={current_user.manage_studio_id}
          onClose={() => setIsOpenAddLessonModal(false)}
          addresses={[]}
          studioName={current_user.studio_name || ''}
        />
      )}
    </>
  );
};

export default Home;
